import React, { useMemo, useState } from 'react';
import DownloadIcon from '@mui/icons-material/Download';

interface Column {
  key: string;
  label: string;
  isNumeric?: boolean;
}

interface DiDataGridProps {
  data: Record<string, any>[];
  itemsPerPage?: number;
}

// Add column display mapping
const COLUMN_LABELS: Record<string, string> = {
  // Priority columns
  company_ticker: 'Ticker',
  company_name: 'Name',
  industry: 'Industry',
  price: 'Price',
  change_percent: 'Change %',

  // Financial ratios
  payback_time: 'Payback Time',
  pe_ratio: 'P/E Ratio TTM',
  ps_ratio: 'P/S Ratio TTM',
  peg_ratio: 'PEG Ratio TTM',
  pb_ratio: 'P/B Ratio TTM',
  dividend_yield_percent: 'Dividend Yield % TTM',

  // Company metrics
  market_cap: 'Market Cap TTM',
  revenue: 'Revenue - annualized',
  net_income: 'Net Income - annualized',
  eps: 'EPS (annualized)',
  roe: 'ROE TTM',
  roic: 'ROIC TTM',
  fcf: 'Free Cash Flow - annualized',
  current_ratio: 'Current Ratio TTM',
  debt_to_equity: 'Debt to Equity TTM',
  payout_ratio: 'Payout Ratio TTM',
  bvps: 'Book Value Per Share TTM',

  // Company information
  country: 'Country',
  industry_group: 'Industry Group',
  stock_exchange_symbol: 'Exchange',
  average_volume: 'Average Volume',
  earnings_announcement: 'Earnings Date',

  // Index membership
  sandp_500: 'S&P 500',
  sandp_400: 'S&P 400',
  russell_3000: 'Russell 3000',
  nasdaq_100: 'NASDAQ 100',

  // Analysis scores
  mos_decision: 'MOS Decision',
  altman_z_score: 'Altman Z-Score',
  piotroski_score: 'Piotroski Score',
  management_score: 'Management Score',
  moat_score: 'Moat Score',
  debt_score: 'Debt Score',
  value_score: 'Value Score',

  // Technical indicators
  close_to_year_high: 'Close to Year High',
  close_to_year_low: 'Close to Year Low',

  // Margins
  net_income_margin: 'Net Income Margin TTM',
  gross_margin: 'Gross Margin TTM',
  operating_margin: 'Operating Margin TTM',

  // Historical data
  seven_years_or_plus_of_financial_history: '7+ Years History',

  // Growth rates - Revenue
  one_year_revenue_growth_rate_cagr: '1Y Revenue Growth',
  three_year_revenue_growth_rate_cagr: '3Y Revenue Growth',
  five_year_revenue_growth_rate_cagr: '5Y Revenue Growth',
  ten_year_revenue_growth_rate_cagr: '10Y Revenue Growth',

  // Growth rates - Net Income
  one_year_net_income_growth_rate_cagr: '1Y Net Income Growth',
  three_year_net_income_growth_rate_cagr: '3Y Net Income Growth',
  five_year_net_income_growth_rate_cagr: '5Y Net Income Growth',
  ten_year_net_income_growth_rate_cagr: '10Y Net Income Growth',

  // Growth rates - Operating Cash Flow
  one_year_operating_cash_flow_growth_rate_cagr: '1Y OCF Growth',
  three_year_operating_cash_flow_growth_rate_cagr: '3Y OCF Growth',
  five_year_operating_cash_flow_growth_rate_cagr: '5Y OCF Growth',
  ten_year_operating_cash_flow_growth_rate_cagr: '10Y OCF Growth',

  // Growth rates - Equity
  one_year_equity_growth_rate_cagr: '1Y Equity Growth',
  three_year_equity_growth_rate_cagr: '3Y Equity Growth',
  five_year_equity_growth_rate_cagr: '5Y Equity Growth',
  ten_year_equity_growth_rate_cagr: '10Y Equity Growth'
};

// Define required first columns
const PRIORITY_COLUMNS = [
  'company_ticker',
  'company_name',
  'industry',
  'price',
  'change_percent',
];

// Add helper function to convert data to CSV
const convertToCSV = (data: Record<string, any>[], columns: Column[]): string => {
  const headers = columns.map(col => col.label).join(',');
  const rows = data.map(row =>
    columns.map(col => {
      const value = row[col.key];
      // Handle values that might contain commas by wrapping in quotes
      return typeof value === 'string' && value.includes(',')
        ? `"${value}"`
        : value;
    }).join(',')
  );
  return [headers, ...rows].join('\n');
};

// Add helper function to format numbers with commas
const formatLargeNumber = (value: number | string): string => {
  if (typeof value === 'string') value = parseFloat(value);
  return value?.toLocaleString() || '';
};

// Add helper function to format dates
const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  return new Date(dateString).toISOString().split('T')[0];
};

// Define financial columns that need number formatting
const FINANCIAL_COLUMNS = [
  'market_cap',
  'revenue',
  'net_income',
  'fcf',
];

// Define date columns
const DATE_COLUMNS = [
  'earnings_announcement',
];

function DiDataGrid({ data, itemsPerPage = 30 }: DiDataGridProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  // Generate columns from the first data item
  const columns: Column[] = useMemo(() => {
    if (data.length === 0) return [];

    const allKeys = Object.keys(data[0]);
    const priorityColumns = PRIORITY_COLUMNS.map(key => ({
      key,
      label: COLUMN_LABELS[key] || key.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' '),
      isNumeric: ['price', 'change_percent'].includes(key)
    }));

    const remainingColumns = allKeys
      .filter(key => !PRIORITY_COLUMNS.includes(key))
      .map(key => ({
        key,
        label: COLUMN_LABELS[key] || key.split('_').map(word =>
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ')
      }));

    return [...priorityColumns, ...remainingColumns];
  }, [data]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig) return data;
    return [...data].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key])
        return sortConfig.direction === 'asc' ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key])
        return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(data.length / itemsPerPage);

  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Add download handler
  const handleDownload = () => {
    const csv = convertToCSV(data, columns);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'stock_screen_results.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <style>
        {`
          .di-table-container {
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
          }

          .di-table-scroll {
            flex: 1;
            overflow-x: auto;
            overflow-y: auto;
          }

          .di-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 0.875rem;
            table-layout: auto;
            border: 1px solid #e5e7eb;
          }

          .di-table th {
            position: sticky;
            top: 0;
            background-color: #f8fafc;
            padding: 0.75rem 1rem;
            text-align: left;
            font-weight: 600;
            color: #4b5563;
            border-bottom: 1px solid #e5e7eb;
            border-right: 1px solid #e5e7eb;
            cursor: pointer;
            z-index: 1;
            border-top: 1px solid #e5e7eb;
            white-space: nowrap;
          }

          .di-table th:first-child {
            border-left: 1px solid #e5e7eb;
          }

          .di-table th:last-child {
            border-right: none;
          }

          .di-table td {
            padding: 0.75rem 1rem;
            color: #1f2937;
            border-bottom: 1px solid #e5e7eb;
            border-right: 1px solid #e5e7eb;
            white-space: nowrap;
          }

          .di-table td:first-child {
            border-left: 1px solid #e5e7eb;
          }

          .di-table td:last-child {
            border-right: none;
          }

          .di-table tbody tr:nth-child(odd) {
            background-color: #f8fafc;
          }

          .di-table tbody tr:hover {
            background-color: #f1f5f9;
          }

          .di-pagination {
            padding: 0.5rem;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            background-color: #fff;
            border-top: 1px solid #e5e7eb;
            gap: 0.5rem;
          }

          .di-button {
            padding: 0.25rem 0.75rem;
            border: 1px solid #e5e7eb;
            border-radius: 0.25rem;
            background-color: white;
            color: #4b5563;
            font-size: 0.875rem;
          }

          .di-button:disabled {
            background-color: #f3f4f6;
            opacity: 0.5;
            cursor: not-allowed;
          }

          .di-sort-icon {
            display: inline-block;
            margin-left: 0.25rem;
          }

          .di-sort-indicator {
            opacity: 0.3;
            margin-left: 0.25rem;
            font-size: 0.75rem;
          }

          th:hover .di-sort-indicator {
            opacity: 0.7;
          }

          .di-table td.numeric {
            text-align: right;
          }

          .positive {
            color: #22c55e;
          }

          .negative {
            color: #ef4444;
          }

          .ticker-link {
            color: #2563eb;
            text-decoration: none;
          }

          .ticker-link:hover {
            text-decoration: underline;
          }

          .di-pagination-info {
            white-space: nowrap;
          }

          .download-button {
            display: flex;
            align-items: center;
            gap: 0.25rem;
          }

          .download-icon {
            font-size: 1.2rem;
          }

          @media (max-width: 640px) {
            .download-text {
              display: none;
            }

            .download-button {
              padding: 0.25rem 0.5rem;
            }
          }
        `}
      </style>
      <div className="di-table-container">
        <div className="di-table-scroll">
          <table className="di-table">
            <thead>
              <tr>
                {columns.map(column => (
                  <th key={column.key} onClick={() => handleSort(column.key)}>
                    {column.label}
                    {sortConfig?.key === column.key ? (
                      <span className="di-sort-icon">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    ) : (
                      <span className="di-sort-indicator">⋮</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map(column => (
                    <td
                      key={column.key}
                      className={column.isNumeric ? 'numeric' : ''}
                    >
                      {column.key === 'company_ticker' ? (
                        <a
                          href={`/ticker/${row[column.key]}`}
                          className="ticker-link"
                        >
                          {row[column.key]}
                        </a>
                      ) : column.key === 'price' || column.key === 'change_percent' ? (
                        <span className={row.change_percent > 0 ? 'positive' : 'negative'}>
                          {row[column.key]}
                        </span>
                      ) : column.key === 'company_name' || column.key === 'industry' ? (
                        <span title={row[column.key]}>
                          {String(row[column.key]).length > 20
                            ? `${String(row[column.key]).substring(0, 20)}...`
                            : row[column.key]}
                        </span>
                      ) : FINANCIAL_COLUMNS.includes(column.key) ? (
                        formatLargeNumber(row[column.key])
                      ) : DATE_COLUMNS.includes(column.key) ? (
                        formatDate(row[column.key])
                      ) : row[column.key] === true ? (
                        '✓'
                      ) : (
                        row[column.key]
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="di-pagination">
          <button
            onClick={handleDownload}
            className="di-button download-button"
            title="Download CSV"
          >
            <DownloadIcon className="download-icon" />
            <span className="download-text">Download CSV</span>
          </button>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="di-button"
          >
            Previous
          </button>
          <span className="di-pagination-info">
            {`${(currentPage - 1) * itemsPerPage + 1}-${Math.min(currentPage * itemsPerPage, data.length)} of ${data.length}`}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="di-button"
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}

export default DiDataGrid;
